/* age-gate.js — age verification modal.
 *
 * Spec: prompt brief + Docs/機能要件定義書.md §4.1.3.
 *  - On DOMContentLoaded: read kosho_age_verified cookie. If true → no-op. Else mount + show.
 *  - "Yes": set cookie kosho_age_verified=true; path=/; max-age=31536000; samesite=lax (1 year).
 *           Close modal with fade-out, restore body scroll.
 *  - "No":  replace modal content with rejection message; no close button; body remains locked.
 *  - Mounts to #age-gate-root (already present in _template.html).
 *
 * Public API:
 *   window.KoshoAgeGate.show()   — force show (e.g., dev override)
 *   window.KoshoAgeGate.hide()   — force hide
 *   window.KoshoAgeGate.reset()  — delete cookie + reload (dev helper)
 *
 * Depends on: focus-trap.js (loaded before this file).
 */

(function (global) {
  'use strict';

  var COOKIE_NAME = 'kosho_age_verified';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year
  // localStorage mirror of the cookie. Cookies are silently dropped at origin
  // "null" (file:// double-click builds), so we persist here too and treat the
  // user as verified if EITHER store says so.
  var STORAGE_KEY = 'kosho_age_verified';
  var STORAGE_VALUE = 'true';

  function readStorage(key) {
    try {
      return global.localStorage ? global.localStorage.getItem(key) : null;
    } catch (err) {
      return null; // access can throw (private mode / disabled storage)
    }
  }

  function writeStorage(key, value) {
    try {
      if (global.localStorage) global.localStorage.setItem(key, value);
    } catch (err) {
      /* ignore — fall back to cookie only */
    }
  }

  function removeStorage(key) {
    try {
      if (global.localStorage) global.localStorage.removeItem(key);
    } catch (err) {
      /* ignore */
    }
  }

  // Verified if EITHER persistence store records acceptance.
  function isVerified() {
    return readCookie(COOKIE_NAME) === 'true' || readStorage(STORAGE_KEY) === STORAGE_VALUE;
  }

  var MOUNT_SELECTOR = '#age-gate-root';
  var rootEl = null;
  var panelEl = null;
  var trap = null;
  var triggerEl = null;
  var isOpen = false;

  function readCookie(name) {
    var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function writeCookie(name, value, maxAgeSec) {
    document.cookie = name + '=' + encodeURIComponent(value) +
      '; path=/; max-age=' + maxAgeSec + '; samesite=lax';
  }

  function deleteCookie(name) {
    document.cookie = name + '=; path=/; max-age=0; samesite=lax';
  }

  function svgIconClose() {
    return '<svg viewBox="0 0 16 16" aria-hidden="true">' +
           '<path d="M3 3 L13 13 M13 3 L3 13"></path></svg>';
  }

  // Sakura petal motif — single 5-petal flower silhouette, ~32x32 viewBox.
  function svgPetal() {
    return '<svg viewBox="0 0 32 32" aria-hidden="true" focusable="false">' +
      '<path d="M16 5c-2.2 0-4 1.7-4 3.7 0 1.2.7 2.3 1.7 3-1.8.3-3.2 1.7-3.2 3.5 0 1.6 1.1 2.9 2.6 3.4-1.5.5-2.6 1.8-2.6 3.4 0 1.8 1.4 3.2 3.2 3.5-1 .7-1.7 1.8-1.7 3 0 2 1.8 3.7 4 3.7s4-1.7 4-3.7c0-1.2-.7-2.3-1.7-3 1.8-.3 3.2-1.7 3.2-3.5 0-1.6-1.1-2.9-2.6-3.4 1.5-.5 2.6-1.8 2.6-3.4 0-1.8-1.4-3.2-3.2-3.5 1-.7 1.7-1.8 1.7-3 0-2-1.8-3.7-4-3.7z"/>' +
      '<circle cx="16" cy="16" r="2.2" fill="var(--color-rose, #C78D86)" opacity="0.6"/>' +
      '</svg>';
  }

  function buildMarkup() {
    return '' +
      '<div class="modal__backdrop" aria-hidden="true"></div>' +
      '<div class="modal__panel" role="dialog" aria-modal="true" aria-labelledby="age-gate-title">' +
        '<button type="button" class="modal__close" data-age-gate-close aria-label="Close">' +
          svgIconClose() +
        '</button>' +
        '<span class="age-gate__petal" aria-hidden="true">' + svgPetal() + '</span>' +
        '<h2 class="modal__title" id="age-gate-title">Welcome to KOSHO SG</h2>' +
        '<p class="modal__subtitle">Are you of legal drinking age?</p>' +
        '<div class="modal__body" data-age-gate-body>' +
          '<div class="modal__actions">' +
            '<button type="button" class="age-gate__btn age-gate__btn--primary" data-age-gate-confirm>' +
              'Yes, I am 20 or older' +
            '</button>' +
            '<button type="button" class="age-gate__btn age-gate__btn--ghost" data-age-gate-deny>' +
              'No, I am not' +
            '</button>' +
          '</div>' +
        '</div>' +
      '</div>';
  }

  function ensureMounted() {
    if (rootEl) return;
    var mount = document.querySelector(MOUNT_SELECTOR);
    rootEl = document.createElement('div');
    rootEl.className = 'modal age-gate';
    rootEl.setAttribute('hidden', '');
    rootEl.innerHTML = buildMarkup();
    if (mount) {
      mount.appendChild(rootEl);
    } else {
      document.body.appendChild(rootEl);
    }
    panelEl = rootEl.querySelector('.modal__panel');
    bindInternal();
    if (global.KoshoFocusTrap) {
      trap = global.KoshoFocusTrap.create(panelEl);
    }
  }

  function bindInternal() {
    rootEl.addEventListener('click', function (e) {
      var t = e.target;
      // The × close button just hides it (treats as "later" acknowledgement).
      // Per spec the gate has only Yes/No options; the × is a courtesy escape
      // that is hidden in the rejected state. It does NOT set the cookie.
      if (t.closest && t.closest('[data-age-gate-close]')) {
        hide();
        return;
      }
      if (t.closest && t.closest('[data-age-gate-confirm]')) {
        confirm();
        return;
      }
      if (t.closest && t.closest('[data-age-gate-deny]')) {
        deny();
        return;
      }
    });
    // ESC: close-via-X equivalent (does NOT set cookie; doesn't apply when rejected)
    rootEl.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !rootEl.classList.contains('is-rejected')) {
        hide();
      }
    });
  }

  function show() {
    ensureMounted();
    if (isOpen) return;
    isOpen = true;
    triggerEl = document.activeElement;
    rootEl.removeAttribute('hidden');
    /* eslint-disable-next-line no-unused-expressions */
    rootEl.offsetHeight;
    rootEl.classList.add('is-open');
    document.body.classList.add('no-scroll');
    if (trap) trap.activate();
  }

  function hide() {
    if (!isOpen || !rootEl) return;
    isOpen = false;
    rootEl.classList.remove('is-open');
    document.body.classList.remove('no-scroll');
    if (trap) trap.deactivate();
    setTimeout(function () {
      if (!isOpen && rootEl) rootEl.setAttribute('hidden', '');
    }, 320);
    if (triggerEl && typeof triggerEl.focus === 'function') {
      try { triggerEl.focus(); } catch (err) { /* ignore */ }
    }
  }

  function confirm() {
    // Persist to BOTH stores so acceptance survives file:// (cookie dropped) and
    // http(s) (localStorage may be cleared independently). hide() unlocks scroll.
    writeStorage(STORAGE_KEY, STORAGE_VALUE);
    writeCookie(COOKIE_NAME, 'true', COOKIE_MAX_AGE);
    hide();
  }

  function deny() {
    if (!rootEl) return;
    rootEl.classList.add('is-rejected');
    var body = rootEl.querySelector('[data-age-gate-body]');
    if (body) {
      body.innerHTML =
        '<div class="age-gate__rejected" role="alert">' +
          '<p>Sorry, you must be of legal age to enter.</p>' +
        '</div>';
    }
    // Body remains scroll-locked. Focus moves to the alert region.
    var alertEl = rootEl.querySelector('.age-gate__rejected');
    if (alertEl) {
      alertEl.setAttribute('tabindex', '-1');
      try { alertEl.focus(); } catch (err) { /* ignore */ }
    }
  }

  function reset() {
    deleteCookie(COOKIE_NAME);
    removeStorage(STORAGE_KEY);
    location.reload();
  }

  function init() {
    if (isVerified()) return;
    ensureMounted();
    show();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  global.KoshoAgeGate = {
    show: show,
    hide: hide,
    reset: reset
  };
})(window);
